import React, { useEffect, useMemo, useState } from "react";

import "./b2bwholesaleorder.css";
import { auth, db } from "../../firebase";
import { get, ref, set } from "firebase/database";

import Header from "../../components/header/Header";
import B2bNav from "../../components/b2bnavbar/B2BNav";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import CancelIcon from '@mui/icons-material/Cancel';
import AnimatedCheck from "../../animations/AnimatedCheck";
import LoadingAnimation from "../../animations/Loading";
import defaultTimsImage from '../../assets/logo_tims.png';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { toast } from "react-toastify";

const B2BWholesaleOrder = () => {
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null); // Track the authenticated user

    //for order form checkout saving
    const [isSaving, setIsSaving] = useState(false);
    const [inputOrderDescription, setInputOrderDescription] = useState('');
    const [inputOrderName, setInputOrderName] = useState('');


    const [isShowOrderDetails, setIsShowOrderDetails] = useState(false);
    const [animationCreateOrder, setAnimationCreateOrder] = useState('hide');

    const [isShowCreateOrder, setIsShowCreateOrder] = useState(false);
    const [expandedOrderProducts, setExpandedOrderProducts] = useState({}); //since multiple orders need to be tracked for animation
    const [hideAnimation, setHideAnimation] = useState('hide');

    const [orders, setOrders] = useState({});
    const [stocks, setStocks] = useState({});
    const [products, setProducts] = useState({});

    const [orderSearchQuery, setOrderSearchQuery] = useState('');
    const [productSearchQuery, setProductSearchQuery] = useState('');

    // table quantity input
    const [opQuanity, setOPQuantity] = useState({}); //since multiple products in table

    //for order selected
    const [selectOrder, setSelectOrder] = useState('');
    //for product selected list
    const [orderingProductList, setOrderingProductList] = useState([]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserId(user.uid); //set uid of the authenticated user
                fetchB2BOrders(user.uid); //fetch b2b orders data for logged user
            } else {
                setUserId(null); //no logged user
            }
        });

        return () => unsubscribe(); //cleanup subscription
    }, []);

    const fetchB2BOrders = async (userId) => {
        setLoading(true);
        try {
            //fetch orders specific to logged-in user
            const ordersRef = ref(db, 'OrdersB2B/' + userId);
            const stocksRef = ref(db, 'Stocks/' + userId);
            const productsRef = ref(db, 'Products/');

            const [ordersSnapshot, stocksSnapshot, productsSnapshot] = await Promise.all([
                get(ordersRef),
                get(stocksRef),
                get(productsRef)
            ]);

            const ordersData = ordersSnapshot.val() || {};
            const reverseOrders = Object.entries(ordersData).reverse();
            const reversedOrdersObject = Object.fromEntries(reverseOrders);

            setOrders(reversedOrdersObject);
            setStocks(stocksSnapshot.val() || {});
            setProducts(productsSnapshot.val() || {});
        } catch (error) {
            console.error('b2bWholesaleorder fetchdata error: ', error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }

    const filteredOrders = useMemo(() => {
        if (!orderSearchQuery) return orders;
        return Object.fromEntries(
            Object.entries(orders).filter(([key, value]) => {
                return (
                    value.order_name?.toLowerCase().includes(orderSearchQuery.toLowerCase()) ||
                    value.order_status?.toLowerCase().includes(orderSearchQuery.toLowerCase())
                );
            })
        );
    }, [orderSearchQuery, orders]);

    //filter products based on productSearchQuery in search text
    const filteredProducts = useMemo(() => {
        if (!productSearchQuery) return products;
        return Object.fromEntries(
            Object.entries(products).filter(([key, value]) => {
                return (
                    value.mpn?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                    value.name?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                    value.supplier?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                    value.type?.toLowerCase().includes(productSearchQuery.toLowerCase())
                );
            })
        );
    }, [productSearchQuery, products]);

    const updateFirbaseComplete = (orderId) => {
        if (!userId) return;
        const orderRef = ref(db, `OrdersB2B/${userId}/${orderId}/order_status`);

        set(orderRef, "Complete")
            .then(() => {
                console.log('updateFirbaseComplete orders b2b status set to complete');

                //update orders list as well
                setOrders((prevOrders) => ({
                    ...prevOrders,
                    [orderId]: {
                        ...prevOrders[orderId],
                        order_status: "Complete"
                    }
                }));
            })
            .catch((error) => {
                console.log('updateFirbaseComplete orders b2b error updating: ', error);
            });
    };

    // show or hide ordered products
    const showOrderedProducts = (orderId) => {
        setExpandedOrderProducts((prev) => ({ ...prev, [orderId]: true })); // Step 1: Mount the component
        setTimeout(() => {
            setHideAnimation((prev) => ({ ...prev, [orderId]: "show" })); // Step 2: Trigger 'show' animation
        }, 10); // Tiny delay to let React mount first
    };

    const hideOrderedProducts = (orderId) => {
        setHideAnimation((prev) => ({ ...prev, [orderId]: "hide" })); // Step 1: Trigger 'hide' animation
        setTimeout(() => {
            setExpandedOrderProducts((prev) => ({ ...prev, [orderId]: false })); // Step 2: Unmount after animation
        }, 600); // Wait for animation duration before unmounting
    };

    //show or hide create order form
    const showCreateOrderForm = () => {
        setIsShowCreateOrder(true);
        setTimeout(() => {
            setAnimationCreateOrder("show");
        }, 10);
    };
    const hideCreateOrderForm = () => {
        setAnimationCreateOrder("hide");
        setTimeout(() => {
            setIsShowCreateOrder(false);
        }, 600);
    };

    //for orderSelected
    const handleOrderSelect = (orderId) => {
        if (!orderId) return;
        const selectedOrder = orders[orderId];

        if (selectedOrder) {
            const newOrderedProducts = selectedOrder.products.map((product) => {
                return {
                    mpn: product.mpn,
                    name: product.name,
                    quantity: product.quantity,
                    price: products[product.mpn]?.wholesale || 0
                };
            });

            // setOrderingProductList((prevList) => {
            //     // Create a copy to avoid direct state mutation issues
            //     const updatedList = [...prevList];

            //     newOrderedProducts.forEach((orderedProduct) => {
            //         const existingProductIndex = updatedList.findIndex(
            //             (p) => p.productId === orderedProduct.productId
            //         );

            //         if (existingProductIndex !== -1) {
            //             console.log("updating...", updatedList[existingProductIndex].quantity, "+", orderedProduct.quantity);
            //             updatedList[existingProductIndex].quantity += orderedProduct.quantity;
            //         } else {
            //             console.log("adding...");
            //             updatedList.push({ ...orderedProduct });
            //         }
            //     });

            //     return updatedList;
            // });

            //update the ordering list
            setOrderingProductList((prevList) => {
                // Create a copy to avoid direct state mutation issues
                const updatedList = new Map(prevList.map(p => [p.mpn, { ...p }]));
                newOrderedProducts.forEach((orderedProduct) => {
                    if (updatedList.has(orderedProduct.mpn)) {
                        const existingProduct = updatedList.get(orderedProduct.mpn);
                        existingProduct.quantity += orderedProduct.quantity;
                    } else {
                        updatedList.set(orderedProduct.mpn, { ...orderedProduct });
                    }
                });
                const finalList = Array.from(updatedList.values());
                console.log("Updated Ordering List:", finalList);

                return finalList;
            });

            setSelectOrder(''); //reset dropdown
        }
    };
    const handleAddToCart = (mpn, product) => {
        const quantity = parseInt(opQuanity[mpn] || 0);
        if (quantity <= 0) {
            return;
        }

        const newOrderedProduct = {
            mpn: mpn,
            name: product.name,
            quantity: quantity,
            price: products[product.mpn]?.wholesale || 0
        };


        setOrderingProductList((prevList) => {
            //create a copy to avoid direct state mutatio issues
            const updatedList = new Map(prevList.map(p => [p.mpn, { ...p }]));
            if (updatedList.has(mpn)) {
                const existingProduct = updatedList.get(mpn);
                existingProduct.quantity += quantity;
            } else {
                updatedList.set(mpn, { ...newOrderedProduct });
            }

            const finalList = Array.from(updatedList.values());
            return finalList;
        });

        setOPQuantity((prev) => ({
            ...prev,
            [mpn]: ""
        }));
    };

    const handleOPQuantityChange = (mpn, value) => {
        // Ensure the value is a number and update the state
        const quantity = Math.max(0, Number(value)); // Avoid negative numbers

        setOPQuantity((prev) => ({
            ...prev,
            [mpn]: quantity
        }));
    };

    const saveToOrdersB2B = () => {
        console.log('saving order...')
        setIsSaving(true);

        // Unix timestamp
        const timestamp = Math.floor(Date.now());

        const b2bOrder = {
            added_date: new Date().toISOString(), // Add the current date and time
            order_description: inputOrderDescription,
            order_id: timestamp.toString(),
            order_name: inputOrderName,
            order_status: "Initiated",
            last_updated_at: timestamp.toString(),
            ordered_by: localStorage.getItem("admin_name"),
            products: orderingProductList
        }

        const ordersB2BRef = ref(db, 'OrdersB2B/' + userId +'/'+timestamp);
        set(ordersB2BRef, b2bOrder)
        .then(() => {
            toast.success('B2B order added successfully')
        })    
        .catch((error) => {
        console.error('Error saving order:', error);
        })
        .finally(() => {
            setIsSaving(false);

            setInputOrderName('');
            setInputOrderDescription('');
            setOrderingProductList([]);
        });
    };

    return (
        <div className="wo-div-main-container">
            {/* top header section */}
            <Header title="Wholesale Order" />

            {/* body section */}
            <div className="wo-div-body">
                {/* left navigation bar */}
                <B2bNav />

                {/* main content section */}
                <div className="wo-div-content">
                    <div className="wo-div-orders-section">
                        <div className="wo-div-orders-header">
                            <input
                                type="text"
                                placeholder="Search Orders by name or status"
                                value={orderSearchQuery}
                                onChange={(e) => setOrderSearchQuery(e.target.value)}
                            />
                            <button onClick={() => showCreateOrderForm()}>Create Order</button>
                        </div>

                        {loading ? (
                            <LoadingAnimation />
                        ) : (
                            <div className="wo-div-orders-list" id="scroll-style">
                                {/* Orders - Grid Layout */}
                                {Object.entries(filteredOrders).map(([orderId, order]) => {
                                    //precompute the total cost
                                    let totalcost = 0;
                                    //let lastUpatedAt = new Date(order.last_updated_at).toLocaleString();
                                    console.log("ujj firebase date" + order.added_date);  // Check what you're getting
                                    //let addDate = new Date(order.added_date).toLocaleString();
                                    const addDate = order.added_date ? new Date(order.added_date).toLocaleString() : "Loading...";
                                    console.log("ujj converted date" + addDate);  // Check what you're getting
                                    let lastUpatedAt = order.last_updated_at ? new Date(order.last_updated_at).toLocaleString() : addDate;

                                    const productList = order.products.map((product, index) => {
                                        const productData = products[product.mpn] || {}; // initialise object at least if empty
                                        const wholesaleCost = productData.wholesale || 0; //initialise to 0 to not available
                                        totalcost += product.quantity * wholesaleCost;

                                        return (
                                            <li key={index}>
                                                {product.name} : {product.quantity} (${products[product.mpn]?.wholesale || 0})
                                            </li>
                                        );
                                    });

                                    return (
                                        <div key={orderId} className="wo-div-order-card">
                                            {/* Header */}
                                            <div className="wo-div-order-card-header">#{orderId}</div>

                                            {/* order-info */}
                                            <div className="wo-div-order-details">
                                                <div className="wo-div-order-products" title="Click for details" onClick={() => showOrderedProducts(orderId)}>
                                                    <span>Products</span>
                                                    <HelpOutlineIcon className="infoicon" />
                                                </div>
                                                <div className="wo-div-order-info">
                                                    <div className="wo-div-order-date">{addDate}</div>
                                                    <div className="wo-div-order-name">{order.order_name}</div>
                                                </div>
                                                <div className="wo-div-order-description">
                                                    <span>{order.order_description}</span>
                                                </div>
                                            </div>

                                            {/* order-status */}
                                            <div className="wo-div-order-status">
                                                {order.order_status === "Delivered" && (
                                                    <div className="wo-div-order-accept" title="Acknowledge Delivery" onClick={() => updateFirbaseComplete(orderId)}>
                                                        <span>Accept</span>
                                                        <AnimatedCheck />
                                                    </div>
                                                )}
                                                <span>{lastUpatedAt}</span>
                                                <div className={`wo-div-order-status-icons ${order.order_status}`}>
                                                    {/* Circles */}
                                                    <div className="wo-div-status-circle"></div>
                                                    <div className="wo-div-status-circle"></div>
                                                    <div className="wo-div-status-circle"></div>
                                                    <div className="wo-div-status-circle"></div>

                                                    {/* Connecting Line */}
                                                    <div className="wo-div-status-line"></div>
                                                </div>
                                                <p className={`${order.order_status}`}>{order.order_status}</p>
                                            </div>

                                            {/* order total cost */}
                                            <div className="wo-div-order-cost">
                                                <span>Total Cost: ${totalcost.toFixed(2)}</span>
                                            </div>

                                            {/* order product details */}
                                            {expandedOrderProducts[orderId] && (
                                                <div className={`wo-order-products-container ${hideAnimation[orderId] || ""}`}>
                                                    <CancelIcon className="cancelIcon" onClick={() => hideOrderedProducts(orderId)}></CancelIcon>
                                                    <div className="white-bg-icon"></div>
                                                    <ul>
                                                        {productList}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {isShowCreateOrder && (
                        <div className={`wo-div-create-orders ${animationCreateOrder}`}>
                            <div className="wo-div-co-header">
                                <div className="wo-div-co-header-top">
                                    <span>Orders form</span>
                                    <ClearIcon className="cancelIcon" onClick={() => hideCreateOrderForm()}></ClearIcon>
                                </div>
                                <div className="wo-div-co-header-base">
                                    <input type="text"
                                        placeholder="Search products by name, mpn, supplier or type"
                                        value={productSearchQuery}
                                        onChange={(e) => setProductSearchQuery(e.target.value)}
                                    />

                                    <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
                                        <option value="" disabled>Classic</option>
                                        <option value="">Steer and drive</option>
                                        <option value="">Steer and dual drive</option>
                                        <option value="">Dual steer and dual drive</option>
                                        <option value="">Steer, drive and trailer-a</option>
                                        <option value="">Dual steer, drive and trailer-a</option>
                                        <option value="">Steer, dual drive and trailer-a</option>
                                        <option value="">Steer and drive, and dual trailer-a</option>
                                        <option value="">Steer, drive, trailer-a and trailer-b</option>
                                    </select>
                                    <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
                                        <option value="" disabled>Compact</option>
                                        <option value="">Steer and drive</option>
                                        <option value="">Steer and dual drive</option>
                                        <option value="">Dual steer and dual drive</option>
                                        <option value="">Steer, drive and trailer-a</option>
                                        <option value="">Dual steer, drive and trailer-a</option>
                                        <option value="">Steer, dual drive and trailer-a</option>
                                        <option value="">Steer and drive, and dual trailer-a</option>
                                        <option value="">Steer, drive, trailer-a and trailer-b</option>
                                    </select>
                                    <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
                                        <option value="" disabled>Advanced</option>
                                        <option value="">Steer and drive</option>
                                        <option value="">Steer and dual drive</option>
                                        <option value="">Dual steer and dual drive</option>
                                        <option value="">Steer, drive and trailer-a</option>
                                        <option value="">Dual steer, drive and trailer-a</option>
                                        <option value="">Steer, dual drive and trailer-a</option>
                                        <option value="">Steer and drive, and dual trailer-a</option>
                                        <option value="">Steer, drive, trailer-a and trailer-b</option>
                                    </select>
                                    <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
                                        <option value="" disabled>Previous orders</option>
                                        {Object.entries(orders).map(([orderId, order]) => (
                                            <option key={orderId} value={orderId}>
                                                {order.order_name} {orderId}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="wo-div-co-body-section">
                                {/* create order products section takes 70% width*/}

                                <div className="wo-div-co-products-section">
                                    <table className="wo-table-products">
                                        <thead>
                                            <tr>
                                                <th>Image</th>
                                                <th className="wo-col-name">Product Name</th>
                                                <th className="wo-col-qty">Qty</th>
                                                <th className="wo-col-cost">Wholesale</th>
                                                <th className="wo-col-action">Action</th>
                                            </tr>
                                        </thead>
                                        {loading ? (
                                            <LoadingAnimation />
                                        ) : (
                                            <tbody className="wo-table-products-body" id="scroll-style">
                                                {Object.entries(filteredProducts).map(([mpn, product]) => (
                                                    <tr key={mpn}>
                                                        <td>
                                                            <img src={product.imageUrl || defaultTimsImage} alt={product.mpn} style={{ width: '100px', height: '100px' }} />
                                                        </td>
                                                        <td className="wo-col-name">{product.name}</td>
                                                        <td className="wo-col-qty">
                                                            <input
                                                                min="0"
                                                                type="number"
                                                                placeholder="0"
                                                                value={opQuanity[mpn] ?? ""}
                                                                onChange={(e) => {
                                                                    const newQuantity = Math.max(0, parseInt(e.target.value, 10) || 0);
                                                                    handleOPQuantityChange(mpn, newQuantity);
                                                                }} />
                                                        </td>
                                                        <td className="wo-col-cost">${product.wholesale || 0}</td>
                                                        <td className="wo-col-action"><button onClick={() => handleAddToCart(mpn, product)}>Add To Cart</button></td>
                                                    </tr>
                                                ))
                                                }
                                            </tbody>
                                        )}

                                    </table>
                                </div>

                                {/* wo check section 30% width*/}
                                <div className="wo-div-co-cart-section">
                                    <span>
                                        <h4>Haulweigh Cart</h4><ShoppingCartIcon />
                                    </span>

                                    {loading ? (
                                        <LoadingAnimation />
                                    ) : (
                                        <div className="wo-div-cart-list">
                                            {/* cart - table layout */}
                                            <table className="wo-table-cart">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th className="wo-col-qty">Qty</th>
                                                        <th className="wo-col-cost">Cost</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="wo-table-cart-body" id="scroll-style">
                                                    {orderingProductList.slice().reverse().map((product, mpn) => {
                                                        //const totalCost = stock.stock_quantity * stock.stock_cost; //calc total cost each
                                                        const totalCost = product.quantity * product.price; //calc total cost each
                                                        return (
                                                            <tr key={mpn}>
                                                                <td>{product.name}</td>
                                                                <td className="wo-col-qty">{product.quantity}</td>
                                                                <td className="wo-col-cost">${product.price}</td>
                                                                <td>${totalCost.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td colSpan="2"><strong>Total Cost</strong></td>
                                                        <td>
                                                            <strong>
                                                                ${Object.entries(orderingProductList).reduce((totalSum, [mpn, product]) => {
                                                                    return totalSum + product.quantity * product.price;
                                                                    //return totalSum + stock.stock_quantity * stock.stock_cost;
                                                                }, 0).toFixed(2)}
                                                            </strong>
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Checkout details Section */}
                            <div className="b2b-div-wo-content-bottom-section">
                                <div className="b2b-div-wo-checkout-body">
                                    <span>
                                        <h4>Checkout details </h4>
                                        <span>this section is checkout details section</span>

                                    </span>
                                    
                                    <div className="b2b-div-wo-checkout-details">
                                        <input 
                                            type="text"
                                            placeholder="Order Name..."
                                            value={inputOrderName}
                                            onChange={(e) => setInputOrderName(e.target.value)}
                                        />

                                        <input 
                                            type="text"
                                            placeholder="Order description..."
                                            value={inputOrderDescription}
                                            onChange={(e) => setInputOrderDescription(e.target.value)}
                                        />
                                    </div>
                                    <button 
                                    className="b2b-div-wo-form-checkout" 
                                    onClick={()=>saveToOrdersB2B()}
                                    disabled={isSaving}>
                                        {isSaving ? 'Saving...': 'Checkout'}
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default B2BWholesaleOrder;